/**
 * ApiService - base class for API services
 *
 * Provides common fetch methods with URL construction and error handling.
 */

import { config } from '@/config';
import { logger } from '@/logging';



export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public endpoint: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export abstract class ApiBase {
  protected sessionId



  constructor(sessionId) {
    this.sessionId = sessionId
  }
  /**
   * Build the full URL for an API endpoint.
   * Format: `${apiUrl}/api/${endpoint}`. Session travels in X-Session-Id.
   */
  protected url(endpoint: string): string {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${config.apiUrl}/api/${normalizedEndpoint}`;
  }

  /** Headers attached to every request — carries the session identity. */
  private headers(extra?: Record<string, string>): Record<string, string> {
    return { 'X-Session-Id': this.sessionId, ...(extra ?? {}) };
  }

  /**
   * GET request
   */
  protected async get<T>(path: string): Promise<T> {
    const url = this.url(path);
    const response = await fetch(url, { headers: this.headers() });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new ApiError(err.error ?? `Request failed`, response.status, path);
    }

    return response.json();
  }



  protected async post<T>(path: string, body): Promise<T> {
    const url = this.url(path);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });

      const data = await response.json().catch(() => ({ error: 'Unknown error' }));

      if (!response.ok) {
        logger.error('ApiService', `${path} failed: ${data.error}`);
      }

      return data;
    } catch (err) {
      logger.error('ApiService', `Network error for ${path}`);
    }
  }
}
